import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { IconComponent } from './ui/icon.component';
import { FooterComponent } from './footer.component';
import { HeaderComponent } from './header.component';
import { ModalComponent } from './modal.component';
import { ModalService } from '../services/modal.service';
import { WhatsappModalService } from '../services/whatsapp-modal.service';
import { BLOG_POSTS, BlogPost } from '../data/blog.data';

@Component({
  selector: 'app-blog-article',
  standalone: true,
  imports: [CommonModule, RouterModule, IconComponent, FooterComponent, HeaderComponent, ModalComponent],
  template: `
    <app-header></app-header>
    <app-modal></app-modal>

    @if (post) {
      <main class="min-h-screen bg-[#111111]">

        <!-- Hero -->
        <div class="relative w-full h-[45vh] md:h-[55vh] overflow-hidden">
          <img [src]="post.image"
               [alt]="post.title"
               class="w-full h-full object-cover object-top"
               width="1920" height="600">
          <!-- Gradient overlay -->
          <div class="absolute inset-0 bg-gradient-to-t from-[#111111] via-[#111111]/50 to-transparent"></div>

          <!-- Back button -->
          <div class="absolute top-24 left-0 right-0 container mx-auto px-4 md:px-6">
            <a routerLink="/blog"
               class="inline-flex items-center gap-2 text-gray-300 hover:text-white text-sm transition-colors bg-black/30 backdrop-blur-sm px-4 py-2 rounded-full border border-white/10 hover:border-white/30">
              <app-icon name="arrow-left" [size]="14"></app-icon>
              Voltar ao Blog
            </a>
          </div>

          <!-- Category & meta -->
          <div class="absolute bottom-0 left-0 right-0 container mx-auto px-4 md:px-6 pb-10">
            <span class="inline-block px-3 py-1 rounded-full text-xs font-bold text-white bg-black/50 backdrop-blur-sm border mb-4"
                  [ngClass]="post.categoryColor">
              {{ post.category }}
            </span>
            <h1 class="text-2xl md:text-4xl lg:text-5xl font-bold text-white leading-tight max-w-4xl mb-4">
              {{ post.title }}
            </h1>
            <div class="flex items-center gap-3 text-gray-400 text-sm">
              <span>{{ post.date }}</span>
              <span class="w-1 h-1 rounded-full bg-gray-600"></span>
              <span>{{ post.readTime }} de leitura</span>
            </div>
          </div>
        </div>

        <!-- Article body -->
        <article class="container mx-auto px-4 md:px-6 max-w-3xl py-16">

          @for (section of post.content; track $index) {

            @if (section.type === 'paragraph') {
              <p class="text-gray-300 text-lg leading-relaxed mb-6">
                {{ section.text }}
              </p>
            }

            @if (section.type === 'heading') {
              <h2 class="text-2xl md:text-3xl font-bold text-white mt-12 mb-5 leading-tight">
                {{ section.text }}
              </h2>
            }

            @if (section.type === 'quote') {
              <blockquote class="my-10 pl-6 border-l-4 border-[#FF5500] bg-[#FF5500]/5 rounded-r-xl py-5 pr-6">
                <p class="text-[#FF5500] text-lg font-semibold italic leading-relaxed">
                  "{{ section.text }}"
                </p>
              </blockquote>
            }

            @if (section.type === 'list' && section.items) {
              <ul class="mb-6 space-y-3">
                @for (item of section.items; track item) {
                  <li class="flex items-start gap-3 text-gray-300 text-base">
                    <span class="flex-shrink-0 mt-1.5 w-2 h-2 rounded-full bg-[#FF5500]"></span>
                    <span>{{ item }}</span>
                  </li>
                }
              </ul>
            }

          }

          <!-- CTA Section -->
          <div class="mt-16 rounded-2xl bg-gradient-to-br from-[#1a1a1a] to-[#111111] border border-white/10 p-8 text-center relative overflow-hidden">
            <div class="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#FF5500] opacity-10 blur-3xl pointer-events-none"></div>
            <div class="absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-[#FF5500] opacity-5 blur-3xl pointer-events-none"></div>
            <div class="relative z-10">
              <h3 class="text-2xl font-bold text-white mb-3">
                Quer saber como aplicar isso na sua empresa?
              </h3>
              <p class="text-gray-400 mb-6 max-w-md mx-auto">
                Fale com um especialista da beetrack e descubra como a tecnologia pode proteger seus colaboradores e reduzir passivos trabalhistas.
              </p>
              <button (click)="openWhatsapp()"
                      class="inline-flex items-center gap-2 bg-[#FF5500] hover:bg-[#e04d00] text-white font-bold px-8 py-4 rounded-full transition-all duration-200 hover:scale-105 shadow-lg hover:shadow-[#FF5500]/30">
                Falar com Especialista
                <app-icon name="arrow-right" [size]="16"></app-icon>
              </button>
            </div>
          </div>

          <!-- Other articles -->
          <div class="mt-16">
            <h3 class="text-xl font-bold text-white mb-6">Outros artigos</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
              @for (other of otherPosts(); track other.id) {
                <a [routerLink]="['/blog', other.slug]"
                   class="flex gap-4 bg-[#1a1a1a] rounded-xl p-4 border border-white/5 hover:border-[#FF5500]/30 transition-all duration-200 group">
                  <img [src]="other.image"
                       [alt]="other.title"
                       class="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                       width="80" height="80" loading="lazy">
                  <div>
                    <span class="text-xs font-bold px-2 py-0.5 rounded-full border text-white"
                          [ngClass]="other.categoryColor">{{ other.category }}</span>
                    <p class="text-white text-sm font-semibold mt-1.5 leading-snug group-hover:text-[#FF5500] transition-colors line-clamp-2">
                      {{ other.title }}
                    </p>
                    <span class="text-gray-500 text-xs mt-1 block">{{ other.readTime }} de leitura</span>
                  </div>
                </a>
              }
            </div>
          </div>

        </article>
      </main>
    } @else {
      <main class="min-h-screen bg-[#111111] flex items-center justify-center pt-24">
        <div class="text-center">
          <p class="text-6xl mb-4">📖</p>
          <h1 class="text-2xl font-bold text-white mb-3">Artigo não encontrado</h1>
          <p class="text-gray-400 mb-6">O artigo que você procura não existe ou foi removido.</p>
          <a routerLink="/blog"
             class="inline-flex items-center gap-2 bg-[#FF5500] text-white font-bold px-6 py-3 rounded-full">
            Ver todos os artigos
          </a>
        </div>
      </main>
    }

    <app-footer></app-footer>
  `,
  styles: [`
    .line-clamp-2 {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  `]
})
export class BlogArticleComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private modalService = inject(ModalService);
  private whatsappModalService = inject(WhatsappModalService);

  post: BlogPost | undefined;

  ngOnInit() {
    this.route.paramMap.subscribe(params => {
      const slug = params.get('slug');
      this.post = BLOG_POSTS.find(p => p.slug === slug);
      if (!this.post) {
        // Scroll to top when post changes
      }
      window?.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  otherPosts(): BlogPost[] {
    return BLOG_POSTS.filter(p => p.slug !== this.post?.slug);
  }

  openModal() {
    this.modalService.open();
  }

  openWhatsapp() {
    const url = 'https://wa.me/5511996586763?text=Ol%C3%A1%2C%20estou%20vindo%20do%20site%20da%20beetrack%20e%20gostaria%20de%20saber%20mais%20sobre%20o%20controle%20da%20pausa%20t%C3%A9rmica';
    this.whatsappModalService.open(url);
  }
}
